import { Avatar } from "@coval/ui/avatar"
import { cn } from "@coval/ui/cn"

import type { ReviewerIdentity } from "@/hooks/useResolveReviewer"
import { getAvatarColor, getAvatarInk } from "@/utils/avatarIdentity"

const SIZE_CLASSES = {
  sm: { avatar: "h-5 w-5", overflow: "h-5 min-w-5 text-xs" },
  md: { avatar: "h-6 w-6", overflow: "h-6 min-w-6 text-sm" },
} as const

/**
 * Overlapping reviewer avatars: the Clerk photo where one is set, otherwise
 * initials on a tint derived from the email, so a reviewer keeps one color
 * everywhere. Past `max`, the rest collapse into a "+N" chip.
 *
 * Purely visual — the caller names the group for assistive tech.
 */
export const ReviewerAvatarStack = ({
  reviewers,
  max = 4,
  size = "md",
  className,
}: {
  reviewers: ReviewerIdentity[]
  max?: number
  size?: keyof typeof SIZE_CLASSES
  className?: string
}) => {
  const shown = reviewers.slice(0, max)
  const overflow = reviewers.length - shown.length
  const classes = SIZE_CLASSES[size]

  return (
    <span
      className={cn("flex items-center -space-x-1.5", className)}
      aria-hidden
    >
      {shown.map((reviewer) => (
        <Avatar
          key={reviewer.email}
          name={reviewer.name}
          src={reviewer.imageUrl}
          tint={getAvatarColor(reviewer.email.toLowerCase())}
          style={{ color: getAvatarInk(reviewer.email.toLowerCase()) }}
          size="sm"
          decorative
          className={cn(classes.avatar, "ring-2 ring-surface-default")}
        />
      ))}
      {overflow > 0 ? (
        <span
          className={cn(
            classes.overflow,
            "z-10 flex items-center justify-center rounded-full bg-surface-1 px-1 font-mono text-coval-text-muted ring-2 ring-surface-default",
          )}
        >
          +{overflow}
        </span>
      ) : null}
    </span>
  )
}
